import { _, dateUtil, type DataOptionItem, IApp } from '@zto/zpage'

import { commonOptions } from '@/config'

export function tryParseJson(text: string, defalutValue?: any) {
  let result = defalutValue

  try {
    result = JSON.parse(text)
  } catch (ex) {}

  return result
}

/** 转换size大小 */
export function pxSize(size?: string | number) {
  if (!size) return 'auto'
  return typeof size === 'number' ? `${size}px` : size
}

/** 检查是否有效短期时间 */
export function checkIsLongTermDate(val: any) {
  if (!val) return true

  const parsedDate = dateUtil.parseValidDate(val)

  // 时间大于2050年
  if (!parsedDate || parsedDate.getFullYear() > 2050) return true

  return false
}

/** 包含长期时间的日期格式化 */
export function formatDate2(val: any) {
  if (checkIsLongTermDate(val)) return '长期有效'
  return dateUtil.parseAndFormat(val, 'date')
}

/** 获取广告计划状态颜色 */
export function getCommonOptionItem(optionName: string | DataOptionItem[], value: any, prop?: string) {
  const optionItems = typeof optionName === 'string' ? (commonOptions as any)[optionName] : optionName
  if (!optionItems) return null

  const optionItem = optionItems.find((it: any) => it.value === value)
  if (!prop || !optionItem) return optionItem
  return optionItem[prop]
}

/**  获取数字范围选项 */
export function getNumberRangeDataOptions(start: number, end: number, fn?: Function | 'stringValue') {
  let nums = []

  for (let i = start; i <= end; i++) {
    nums.push(i)
  }

  return getArrayDataOptions(nums, fn)
}

/** 获取数组选项 */
export function getArrayDataOptions(arr: number[] | string[], fn?: Function | 'stringValue') {
  if (fn === 'stringValue') {
    fn = (it: number | string) => ({ label: String(it), value: String(it) })
  } else {
    fn = fn || ((it: number | string) => ({ label: String(it), value: it }))
  }

  return arr.map(fn as any)
}

/** 排序请求转换 */
export function sortResponseOptionsTransformFn(prop?: string | null, transformType?: string) {
  const typeParts = transformType ? transformType.split(':') : ['code', 'name']

  return (data: any): DataOptionItem[] => {
    const items = prop ? data[prop] : Array.isArray(data) ? data : data?.data

    return (items || []).map((v: any) => {
      return { [typeParts[0]]: v, [typeParts[1]]: String(v) }
    })
  }
}

/** 获取配置选项 */
export function configResponseOptionsTransformFn(key: string, transformer: Function | 'json' | 'list' = 'list') {
  return (data: any): DataOptionItem[] => {
    const detailItem = (data?.details || []).find((it: any) => it.keyCode === key)
    const detailValueStr: string = (detailItem?.value || '').trim()

    let items: any[] = []

    if (_.isFunction(transformer)) {
      items = (transformer as Function)(detailValueStr, data)
    } else if (detailValueStr) {
      if (transformer === 'json') {
        try {
          items = JSON.parse(detailValueStr)
        } catch (ex) {}
      } else if (transformer === 'list') {
        items = detailValueStr.split(',').map(it => {
          const s = (it || '').trim()
          return { name: s, code: s, label: s, value: s }
        })
      }
    }

    return items
  }
}

export interface ParsePayloadJsonDataOptions {
  prop?: string // jsonData
  stringifyPayload?: boolean // jsonData是否字符串
  parsePropPrefix?: string | boolean // 解析后字段前缀 默认jsonData
  mergePayload?: boolean // 是否合并进原payload，默认合并
}

export interface ExtractPayloadJsonDataOptions extends ParsePayloadJsonDataOptions {
  clearPayload?: boolean // 是否清理原jsonData前缀
}

export function normalizePayloadJsonDataOptions(options?: ParsePayloadJsonDataOptions | ExtractPayloadJsonDataOptions) {
  if (options && !options.parsePropPrefix && options.prop) {
    options.parsePropPrefix = options.prop
  }

  options = Object.assign(
    { prop: 'jsonData', parsePropPrefix: 'jsonData', mergePayload: true, clearPayload: true, stringifyPayload: true },
    options
  )

  if (options.parsePropPrefix === true) options.parsePropPrefix = 'jsonData'

  if (typeof options.parsePropPrefix !== 'string') options.parsePropPrefix = ''

  return options
}

/** 解析请求内jsonData字符串 */
export function parsePayloadJsonData(payload: any, options?: ParsePayloadJsonDataOptions) {
  options = normalizePayloadJsonDataOptions(options)

  const parsePropPrefix = options.parsePropPrefix
  const jsonDataStr = payload?.[options.prop!]

  if (!jsonDataStr || !parsePropPrefix) return

  let jsonDataObj: any = {}

  if (options.stringifyPayload) {
    try {
      jsonDataObj = JSON.parse(jsonDataStr)
    } catch (err) {
      return
    }
  }

  let parseResult: any = {}

  Object.entries(jsonDataObj).forEach(([key, value]) => {
    parseResult[`${parsePropPrefix}_${key}`] = value
  })

  if (options.mergePayload) {
    Object.assign(payload, parseResult)
  }

  return parseResult
}

/** 组合payload */
export function extractPayloadJsonData(payload: any, options?: ExtractPayloadJsonDataOptions) {
  options = normalizePayloadJsonDataOptions(options)
  const parsePropPrefix = options.parsePropPrefix

  if (!payload || !parsePropPrefix) return

  const parsePropPrefixStr = `${parsePropPrefix}_`
  const parsePropPrefixStrLength = parsePropPrefixStr.length

  const extPayload = _.deepClone(payload)
  const extData: any = {}

  Object.entries(extPayload).forEach(([key, value]) => {
    if (key.startsWith(parsePropPrefixStr)) {
      const subKey = key.substring(parsePropPrefixStrLength)
      extData[subKey] = value

      if (options!.clearPayload) delete extPayload[key]
    }
  })

  if (options?.mergePayload) {
    let extDataValue = extData

    if (options.stringifyPayload) {
      extDataValue = extData ? JSON.stringify(extData) : null
    }

    extPayload[options.prop!] = extDataValue
  }

  return extPayload
}

export enum ConfigEnumOptionsValueType {
  TEXT = 'TEXT',
  LIST = 'LIST'
}

/** 已解析配置项 */
export interface ParsedConfigEnumOptions {
  keyCode: string
  valueType: ConfigEnumOptionsValueType
  index: number
  options: DataOptionItem[]
  rawConfig: any
}

/** 解析配置枚举详情 */
export function parseConfigEnumOptions(config: any, keyCode: string): ParsedConfigEnumOptions {
  const rawConfig = _.deepClone(config)

  const details = rawConfig.details || []
  const index = details.findIndex((it: any) => it.keyCode === keyCode)
  const detail = details[index]
  const parsedDetail = parseConfigEnumOptionsDetail(detail)

  return {
    keyCode,
    index,
    rawConfig,
    valueType: parsedDetail.valueType,
    options: parsedDetail.options
  }
}

/** 解析配置枚举详情 */
export function parseConfigEnumOptionsDetail(detail: any) {
  const detailValue = detail.value

  const parsedDetail = { valueType: ConfigEnumOptionsValueType.TEXT, options: [] }

  if (!detailValue) return parsedDetail

  if (detail.valueType === ConfigEnumOptionsValueType.LIST) {
    parsedDetail.valueType = detail.valueType

    parsedDetail.options = tryParseJson(detailValue, [])
  } else {
    parsedDetail.options = detailValue.split(',').map((it: any) => {
      const v = it.trim()
      return { label: v, value: v }
    })
  }

  if (!Array.isArray(parsedDetail.options)) {
    parsedDetail.options = []
  }

  return parsedDetail
}

/** 编译配置枚举 */
export function compileConfigEnumOptionsDetailValue(parsedOptions: ParsedConfigEnumOptions, newOptions: any[]) {
  if (!parsedOptions?.rawConfig || !newOptions) return ''
  if (!parsedOptions.rawConfig.details) return ''

  const rawConfig = parsedOptions.rawConfig
  const detailIndex = parsedOptions.index

  const rawDetails = _.deepClone(rawConfig.details || [])
  const rawDetail = rawDetails[detailIndex]

  if (!rawDetail) return ''

  let detailValue = ''

  if (parsedOptions.valueType === ConfigEnumOptionsValueType.LIST) {
    detailValue = JSON.stringify(newOptions)
  } else {
    detailValue = newOptions
      .map(it => it.value)
      .filter(it => !!it)
      .join()
  }

  return detailValue
}

/** 获取系统配置 */
export async function requestSystemConfig(app: IApp, configCode: string, keyCode?: string) {
  const res = await app.request({
    api: 'tuxi.dms.config.queryConfig',
    params: { configCode }
  })

  const cfg = parseSystemConfigData(res)

  if (!keyCode) return cfg

  return cfg?.[keyCode]
}

/** 解析系统配置数据 */
export function parseSystemConfigData(cfg: any): Record<string, any> {
  if (!cfg?.details?.length) return {}

  const cfgData = cfg.details.reduce((acc: any, cur: any) => {
    if (cur?.keyCode) {
      acc[cur.keyCode] = cur
    }
    return acc
  }, {})

  return cfgData
}

/** 获取系统配置详情 */
export function getSystemConfigDetail(cfg: any, keyCode: string) {
  if (!cfg?.details?.length) return null

  const detail = (cfg?.details || []).find((it: any) => it.keyCode === keyCode)
  return detail
}

export async function queryNavigatorPermissions(name: string) {
  if (!navigator.permissions) return false

  const res = await navigator.permissions.query({ name: name as any })

  return res.state == 'granted' || res.state == 'prompt'
}

/** 设置剪切板文本 */
export async function setClipboardText(text: string) {
  try {
    // 尝试直接调用
    await navigator.clipboard.writeText(text)
  } catch (err) {
    // 尝试采用老方法
    const aux = document.createElement('textarea')
    aux.setAttribute('value', text) // 设置元素内容
    document.body.appendChild(aux) // 将元素插入页面进行调用
    aux.select() // 复制内容
    document.execCommand('copy') // 将内容复制到剪贴板
    document.body.removeChild(aux) // 删除创建元素
  }
}

/** 清理区域列表请求 */
export function clearAreaListRequest(prop: string) {
  return (req: any) => {
    const payload = { ...req }

    if (payload[prop]?.length) {
      const areaList = payload[prop].filter((v: any) => !_.isNil(v))
      if (!areaList.length) payload[prop] = null
    }

    return payload
  }
}
